'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import InteractiveMap, { MapMarker } from '@/components/InteractiveMap';

interface Destination {
  id: string;
  name: string;
  region: string;
  province: string;
  tag: string;
  packages_count: number;
  is_featured: boolean;
  image_url?: string;
  description?: string;
}

// Demo fallback destinations when DB is empty
const DEMO_DESTINATIONS: Destination[] = [
  { id: 'demo-dest-1', name: 'Everest Base Camp', region: 'Solukhumbu', province: 'Koshi', tag: 'Trekking', packages_count: 48, is_featured: true, image_url: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80', description: 'The ultimate trekking destination — base of the world\'s highest peak.' },
  { id: 'demo-dest-2', name: 'Pokhara', region: 'Kaski', province: 'Gandaki', tag: 'Scenic', packages_count: 62, is_featured: true, image_url: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', description: 'Gateway to the Annapurna range with stunning Phewa Lake views.' },
  { id: 'demo-dest-3', name: 'Kathmandu', region: 'Kathmandu', province: 'Bagmati', tag: 'Cultural', packages_count: 85, is_featured: true, image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', description: 'Nepal\'s capital — a UNESCO World Heritage city of temples and culture.' },
  { id: 'demo-dest-4', name: 'Chitwan', region: 'Chitwan', province: 'Bagmati', tag: 'Wildlife', packages_count: 34, is_featured: true, image_url: 'https://images.unsplash.com/photo-1549366021-9f761d450615?w=600&q=80', description: 'Home to one-horned rhinos and Bengal tigers in the Terai jungle.' },
  { id: 'demo-dest-5', name: 'Mustang', region: 'Mustang', province: 'Gandaki', tag: 'Adventure', packages_count: 19, is_featured: false, image_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', description: 'The forbidden kingdom — ancient Tibetan culture in a desert landscape.' },
  { id: 'demo-dest-6', name: 'Lumbini', region: 'Rupandehi', province: 'Lumbini', tag: 'Spiritual', packages_count: 22, is_featured: false, image_url: 'https://images.unsplash.com/photo-1605640840605-14ac1855827b?w=600&q=80', description: 'Birthplace of Lord Buddha — a sacred pilgrimage site for millions.' },
  { id: 'demo-dest-7', name: 'Rara Lake', region: 'Mugu', province: 'Karnali', tag: 'Scenic', packages_count: 11, is_featured: false, image_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80', description: 'Nepal\'s largest lake — a pristine gem in the remote Karnali region.' },
  { id: 'demo-dest-8', name: 'Annapurna', region: 'Kaski', province: 'Gandaki', tag: 'Trekking', packages_count: 57, is_featured: true, image_url: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=600&q=80', description: 'The Annapurna Circuit — one of the world\'s greatest trekking routes.' },
];

const NEPAL_PROVINCES = ['All Provinces', 'Koshi', 'Madhesh', 'Bagmati', 'Gandaki', 'Lumbini', 'Karnali', 'Sudurpashchim'];

const DEST_COORDS: Record<string, { lat: number; lng: number }> = {
  'Everest Base Camp': { lat: 27.9881, lng: 86.9250 },
  'Pokhara': { lat: 28.2096, lng: 83.9856 },
  'Kathmandu': { lat: 27.7172, lng: 85.3240 },
  'Chitwan': { lat: 27.5291, lng: 84.3542 },
  'Lumbini': { lat: 27.4833, lng: 83.2762 },
  'Mustang': { lat: 28.9965, lng: 83.8500 },
  'Annapurna': { lat: 28.5967, lng: 83.8200 },
  'Rara Lake': { lat: 29.5200, lng: 82.0800 },
};

export default function DestinationsPage() {
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('All Provinces');
  const [showMap, setShowMap] = useState(false);
  const [usingDemoData, setUsingDemoData] = useState(false);

  useEffect(() => {
    const fetchDestinations = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('destinations')
          .select('*')
          .order('packages_count', { ascending: false });

        if (error) {
          console.warn('[YatraNest Destinations] Query failed:', error.message);
          setDestinations(DEMO_DESTINATIONS);
          setUsingDemoData(true);
        } else if (!data || data.length === 0) {
          setDestinations(DEMO_DESTINATIONS);
          setUsingDemoData(true);
        } else {
          setDestinations(data);
          setUsingDemoData(false);
        }
      } catch (err) {
        console.warn('[YatraNest Destinations] Unexpected error:', (err as any)?.message);
        setDestinations(DEMO_DESTINATIONS);
        setUsingDemoData(true);
      }
      setLoading(false);
    };
    fetchDestinations();
  }, []);

  const filtered = destinations.filter(d => {
    const matchSearch = !search ||
      d.name?.toLowerCase().includes(search.toLowerCase()) ||
      d.region?.toLowerCase().includes(search.toLowerCase()) ||
      d.province?.toLowerCase().includes(search.toLowerCase()) ||
      d.tag?.toLowerCase().includes(search.toLowerCase());
    const matchProvince = selectedProvince === 'All Provinces' || d.province === selectedProvince;
    return matchSearch && matchProvince;
  });

  const featured = filtered.filter(d => d.is_featured);
  const rest = filtered.filter(d => !d.is_featured);

  const tagColors: Record<string, string> = {
    'Trekking': 'bg-green-400/10 text-green-400 border-green-400/20',
    'Cultural': 'bg-gold/10 text-gold border-gold/20',
    'Adventure': 'bg-red-400/10 text-red-400 border-red-400/20',
    'Wildlife': 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    'Spiritual': 'bg-purple-400/10 text-purple-400 border-purple-400/20',
    'Scenic': 'bg-sky/10 text-sky border-sky/20',
  };

  const getCoords = (d: Destination) => {
    const exact = DEST_COORDS[d.name];
    if (exact) return exact;
    // Approximate by province centroid
    const provinceCentroids: Record<string, { lat: number; lng: number }> = {
      'Koshi': { lat: 27.3, lng: 87.1 },
      'Madhesh': { lat: 26.8, lng: 85.9 },
      'Bagmati': { lat: 27.7, lng: 85.3 },
      'Gandaki': { lat: 28.2, lng: 84.0 },
      'Lumbini': { lat: 27.7, lng: 83.4 },
      'Karnali': { lat: 29.0, lng: 82.2 },
      'Sudurpashchim': { lat: 29.3, lng: 80.6 },
    };
    return provinceCentroids[d.province] || { lat: 28.3949, lng: 84.124 };
  };

  const mapMarkers: MapMarker[] = filtered.map(d => {
    const coords = getCoords(d);
    return {
      id: d.id,
      lat: coords.lat,
      lng: coords.lng,
      title: d.name,
      description: `${d.region} · ${d.province}`,
      category: 'destination',
      link: `/destinations/${d.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`,
    };
  });

  const getDestSlug = (dest: Destination) => {
    if (dest.slug) return dest.slug;
    return dest.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const DestCard = ({ dest }: { dest: Destination }) => (
    <Link href={`/destinations/${getDestSlug(dest)}`}
      className="yn-card rounded-2xl overflow-hidden hover:border-gold/30 transition-all group block">
      <div className="h-40 bg-gradient-to-br from-navy-mid to-navy relative overflow-hidden">
        {dest.image_url ? (
          <img src={dest.image_url} alt={`${dest.name} destination in Nepal`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-5xl opacity-30">🏔️</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
        {dest.is_featured && (
          <div className="absolute top-3 right-3 bg-gold text-navy text-[10px] font-bold px-2 py-0.5 rounded-full">⭐ Featured</div>
        )}
        <div className="absolute bottom-3 left-3">
          <h3 className="text-snow font-bold text-lg leading-tight">{dest.name}</h3>
          <p className="text-snow/70 text-xs">{dest.region} · {dest.province}</p>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          {dest.tag && (
            <span className={`text-xs px-2 py-0.5 rounded-full border ${tagColors[dest.tag] || 'bg-white/5 text-snow/60 border-white/10'}`}>
              {dest.tag}
            </span>
          )}
          <span className="text-snow/40 text-xs">{dest.packages_count || 0} packages</span>
        </div>
        {dest.description && (
          <p className="text-snow/60 text-xs line-clamp-2 mb-3">{dest.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-gold text-xs font-medium group-hover:underline">View Destination →</span>
        </div>
      </div>
    </Link>
  );

  return (
    <main className="min-h-screen bg-navy">
      <Header />
      <div className="pt-24 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 mb-10">
          <div className="text-center mb-8">
            <h1 className="text-snow font-bold text-3xl md:text-4xl mb-3">Explore Nepal&apos;s Destinations</h1>
            <p className="text-snow/60 text-lg max-w-2xl mx-auto">From the Himalayas to the Terai plains — discover every corner of the world&apos;s most diverse country.</p>
          </div>

          {/* Demo data notice */}
          {usingDemoData && (
            <div className="mb-4 max-w-3xl mx-auto px-4 py-2.5 rounded-xl bg-gold/10 border border-gold/20 text-gold/80 text-xs flex items-center gap-2">
              <span>ℹ️</span>
              <span>Showing demo destinations — live data will appear once destinations are added to the database.</span>
            </div>
          )}

          {/* Filters */}
          <div className="yn-card rounded-2xl p-4 mb-4 max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Province filter */}
              <div className="relative flex-1">
                <select
                  value={selectedProvince}
                  onChange={e => setSelectedProvince(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-snow text-sm focus:outline-none focus:border-gold/50 appearance-none pr-8"
                >
                  {NEPAL_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                  <svg className="w-3.5 h-3.5 text-snow/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {/* Search */}
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setSearch('')}
                placeholder="Search destinations, regions..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40 transition-colors text-sm"
              />
              {/* Map toggle */}
              <button
                onClick={() => setShowMap(m => !m)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl border text-sm font-medium transition-all whitespace-nowrap ${showMap ? 'bg-gold/20 border-gold/50 text-gold' : 'bg-white/5 border-white/10 text-snow/70 hover:text-snow hover:border-gold/30'}`}
              >
                🗺️ {showMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
            {/* Active filters */}
            {(selectedProvince !== 'All Provinces' || search) && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {selectedProvince !== 'All Provinces' && (
                  <span className="text-xs bg-gold/10 text-gold border border-gold/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                    📍 {selectedProvince}
                    <button onClick={() => setSelectedProvince('All Provinces')} className="ml-1 hover:text-gold/60">✕</button>
                  </span>
                )}
                {search && (
                  <span className="text-xs bg-white/5 text-snow/60 border border-white/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                    🔍 "{search}"
                    <button onClick={() => setSearch('')} className="ml-1 hover:text-snow/40">✕</button>
                  </span>
                )}
                <button onClick={() => { setSearch(''); setSelectedProvince('All Provinces'); }} className="text-xs text-snow/40 hover:text-gold transition-colors">Clear all</button>
              </div>
            )}
          </div>
        </div>

        {/* Interactive Map */}
        {showMap && (
          <div className="max-w-7xl mx-auto px-4 mb-10">
            <div className="yn-card rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-gold text-lg">🗺️</span>
                <h2 className="text-snow font-bold text-lg">Destinations Map</h2>
                <span className="text-snow/40 text-xs ml-auto">{filtered.length} destinations shown</span>
              </div>
              <InteractiveMap
                markers={mapMarkers}
                centerLat={28.3949}
                centerLng={84.124}
                zoom={7}
                height="500px"
                showControls
                showNearbySearch
                showRoutePlanning
              />
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-64 bg-white/5 rounded-2xl animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">🗺️</div>
              <h3 className="text-snow font-semibold mb-2">No destinations found</h3>
              <p className="text-snow/40 text-sm">Try adjusting your search or province filter</p>
              <button onClick={() => { setSearch(''); setSelectedProvince('All Provinces'); }}
                className="mt-4 text-gold text-sm hover:underline">Clear all filters</button>
            </div>
          ) : (
            <>
              {featured.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-snow font-bold text-xl mb-4">⭐ Featured Destinations</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {featured.map(d => <DestCard key={d.id} dest={d} />)}
                  </div>
                </div>
              )}
              {rest.length > 0 && (
                <div>
                  {featured.length > 0 && <h2 className="text-snow font-bold text-xl mb-4">All Destinations</h2>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {rest.map(d => <DestCard key={d.id} dest={d} />)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
}
